import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { describeError } from '../../ui/problemDetails.ts';
import { deleteList, listKeys, updateList, type ShoppingList } from './itemsApi.ts';

interface ListSettingsProps {
  groupId: string;
  list: ShoppingList;
  /** How many lists the group has — the last one cannot be deleted. */
  listCount: number;
}

/**
 * Rename the list showing, make it the group's default, or delete it.
 *
 * Acts on the selected list rather than offering a picker: you chose it in the switcher
 * moments ago, and a second picker here would only be a way to rename the wrong thing.
 */
export function ListSettings({ groupId, list, listCount }: ListSettingsProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(list.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKeys.ofGroup(groupId) });

  const save = useMutation({
    mutationFn: (next: {
      name: string;
      isDefault: boolean;
      assignNewItemsToCreator: boolean;
      preserveItemNotes: boolean;
      groupByAisle: boolean;
    }) => updateList(list.id, next),
    onSuccess: invalidate,
  });

  /** Every field the list has, so a save of one cannot silently reset the others. */
  const current = {
    name: list.name,
    isDefault: list.isDefault,
    assignNewItemsToCreator: list.assignNewItemsToCreator,
    preserveItemNotes: list.preserveItemNotes,
    groupByAisle: list.groupByAisle,
  };

  const remove = useMutation({
    mutationFn: () => deleteList(list.id),
    onSuccess: invalidate,
  });

  const trimmed = name.trim();
  const error = save.error ?? remove.error;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      save.mutate({ ...current, name: trimmed });
    }
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">This list</h3>

      <form className="settings-row" onSubmit={handleSubmit}>
        <input
          className="settings-row__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label="List name"
          maxLength={80}
          required
        />
        <button
          className="button button--primary button--small"
          type="submit"
          disabled={save.isPending || trimmed === list.name || trimmed.length === 0}
        >
          Save
        </button>
      </form>

      {/* Applied on the tap rather than waiting for Save, matching every other switch in
          the app. The setting only decides what *future* items get; nothing already on
          the list moves, because retrospectively unassigning a shop someone has divided
          up would be a much larger thing than a checkbox implies. */}
      <label className="toggle">
        <input
          type="checkbox"
          checked={list.assignNewItemsToCreator}
          onChange={(event) =>
            save.mutate({ ...current, assignNewItemsToCreator: event.target.checked })
          }
          disabled={save.isPending}
        />
        <span>
          Assign new items to whoever adds them
          <span className="toggle__hint">
            {list.assignNewItemsToCreator
              ? 'Added items say who they’re for. Anyone can still tick anything off.'
              : 'Added items are for anyone. You can still assign one from the pen icon.'}
          </span>
        </span>
      </label>

      {/* Applied on the tap for the same reason, and it likewise only decides what future
          adds get: a note already written stays written whichever way this is set. */}
      <label className="toggle">
        <input
          type="checkbox"
          checked={list.preserveItemNotes}
          onChange={(event) => save.mutate({ ...current, preserveItemNotes: event.target.checked })}
          disabled={save.isPending}
        />
        <span>
          Remember notes for things you buy again
          <span className="toggle__hint">
            {list.preserveItemNotes
              ? 'Adding “Milk” again brings its note — “4 pints” — back with it.'
              : 'Added items start with a blank note, even ones you’ve bought before.'}
          </span>
        </span>
      </label>

      {/* The one setting here that changes how the list is *drawn* rather than what an add
          does. It is shared like the other two, which is the trade to know about: one
          person turning it off turns it off for the household. That is the right way round
          for a list two people read in the same shop — a per-device preference would mean
          "top of the list" meant different rows to each of them while they stood next to
          each other. */}
      <label className="toggle">
        <input
          type="checkbox"
          checked={list.groupByAisle}
          onChange={(event) => save.mutate({ ...current, groupByAisle: event.target.checked })}
          disabled={save.isPending}
        />
        <span>
          Group items by aisle
          <span className="toggle__hint">
            {list.groupByAisle
              ? 'Fruit & veg, bakery, cleaning… so you walk the shop once. Tap the pen to move anything the app files wrongly.'
              : 'One list, in the order things were added.'}
          </span>
        </span>
      </label>

      <div className="settings-actions">
        {!list.isDefault && (
          <button
            type="button"
            className="link-button"
            onClick={() => save.mutate({ ...current, isDefault: true })}
            disabled={save.isPending}
          >
            Open this list by default
          </button>
        )}

        {/* The API refuses to delete a group's only list; saying so is friendlier than
            letting someone press a button and read an error. */}
        {listCount > 1 &&
          (confirmingDelete ? (
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
            >
              Really delete “{list.name}” and everything on it?
            </button>
          ) : (
            <button
              type="button"
              className="link-button link-button--danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete list
            </button>
          ))}
      </div>

      {list.isDefault && listCount > 1 && (
        <p className="settings-hint">This is the list the app opens.</p>
      )}

      {listCount === 1 && (
        <p className="settings-hint">The group&rsquo;s only list, so it can&rsquo;t be deleted.</p>
      )}

      {error && (
        <p className="form-error" role="alert">
          {describeError(error)}
        </p>
      )}

      {save.isSuccess && !save.isPending && <p className="settings-status">Saved.</p>}
    </section>
  );
}
